import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
} from 'react';
import { getOddsFormatIs } from '~/vendor/cx-web/domain-types';
import { addToBetBasketCookie, CookieService, removeFromBetBasketCookie } from '~/vendor/cx-web/feature-cookies';
import {
  buildAddToBetslipEvent,
  buildBetPlacedEvent,
  buildBetSlipClearEvent,
  buildBetSlipCloseEvent,
  buildChangeBookieSelectionEvent,
  buildRemoveBetEvent,
  trackBetslipEvent,
} from '~/vendor/cx-web/feature-tracking/betslip';
import { getRepubMetaFromRepub } from '~/vendor/cx-web/utils';

import {
  type BetslipStore,
  createBetslipStore,
  getActiveBetslipStore,
  getBetslipSnapshot,
  setActiveBetslipStore,
} from '../store/betslip-store';

import { useBetslipUtils } from './hooks/use-betslip-utils';
import { calculateBookieOddsAndSelectedBookieOdds } from './utils/betslip-utils';
import { clickout } from './clickout';
import { CLICKOUT_ERROR_REASONS, getClickoutErrorCode } from './clickout-errors';
import LocalStorageService from './localstorage-actions';
import {
  type Bet,
  type BetslipProviderData,
  type BetslipProviderProps,
  type BetslipState,
  type BookieOdds,
  type CallbackFn,
  type OddsFormatIs,
} from './types';

const BETSLIPDATA = 'betslip';
const ONE_DAY = 86_400_000; // milliseconds in a day

export type LastFetchStatus = { at: number; ok: boolean; reason?: string };

const isCxDebugEnabled = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    const fromQuery = new URLSearchParams(window.location.search).get('cxDebug');
    if (fromQuery && fromQuery !== '0' && fromQuery !== 'false') return true;
    return document.cookie.split(';').some(c => c.trim().startsWith('cxDebug=1'));
  } catch {
    return false;
  }
};

export const BetslipContext = createContext<BetslipProviderData>(undefined as any as BetslipProviderData);
BetslipContext.displayName = 'BetslipContext';

export const BetslipProvider = ({
  betBasketCookieScope,
  bookmakers,
  children,
  countryCode,
  onBetslipOpenChange,
  persistedBetslipData,
  repub,
  subdivisionCode,
  userOddsFormat,
  ...props
}: PropsWithChildren<BetslipProviderProps>) => {
  // Derived from `repub` rather than passed separately so it can never drift from it — mirrors how
  // RepubProvider itself computes repubIs.
  const { repubIs } = useMemo(() => getRepubMetaFromRepub(repub), [repub]);

  // Held in a ref so the open/close callbacks below keep stable identities and dependency arrays,
  // matching the previous static import — callers don't need to memoize onBetslipOpenChange.
  const onBetslipOpenChangeRef = useRef(onBetslipOpenChange);
  useEffect(() => {
    onBetslipOpenChangeRef.current = onBetslipOpenChange;
  }, [onBetslipOpenChange]);

  const cookieService = useMemo(() => new CookieService(), []);
  const localStorageService = useMemo(() => new LocalStorageService(), []);

  const oddsFormatIs: OddsFormatIs = useMemo(() => getOddsFormatIs(userOddsFormat), [userOddsFormat]);

  // NOTE: betslipUtils encompasses helpers which do not mutate local state
  const betslipUtils = useBetslipUtils(repub);

  // Betslip state lives in a framework-agnostic Zustand vanilla store instead of useState, so it can be
  // read and written outside React. One store per provider (created lazily here) keeps per-request SSR
  // state isolated exactly as useState did; the provider is now a thin adapter that reads via
  // useSyncExternalStore and writes via store.setState.
  const storeRef = useRef<BetslipStore | null>(null);
  if (!storeRef.current) {
    storeRef.current = createBetslipStore({
      betslip: persistedBetslipData?.betslip || [],
      bookieOdds: persistedBetslipData?.bookieOdds || [],
      selectedBookie: persistedBetslipData?.selectedBookie || null,
      stake: Number(persistedBetslipData?.stake || 10),
    });
  }
  const store = storeRef.current;

  // Publish the live store to non-React consumers (Astro islands, diagnostics). The client mounts a
  // single provider, so this pointer always references the active betslip.
  useEffect(() => {
    setActiveBetslipStore(store);
    // Clear only if we're still the published store. Guards the transient double-mount window where
    // React runs a new provider's effect before the old provider's cleanup, so the unmounting one
    // doesn't null out a pointer the live provider already owns.
    return () => {
      if (getActiveBetslipStore() === store) setActiveBetslipStore(null);
    };
  }, [store]);

  // One subscription to the whole store. The provider re-renders on any field change (it consumes all
  // of them anyway), so per-field granularity bought nothing. getState is a stable reference and
  // returns the same state object until the next setState, satisfying useSyncExternalStore's snapshot
  // contract on both client and server.
  const storeState = useSyncExternalStore(store.subscribe, store.getState, store.getState);
  // `added` and `loading` are intentionally not destructured here — they are not consumed directly in
  // this scope and instead flow through `getBetslipSnapshot(storeState)` below into `betslipState`.
  const { betslip, bookieOdds, open, selectedBookie, stake } = storeState;

  // Ensure we sync betslip cookies
  useEffect(() => {
    if (!betslip.length) {
      cookieService.select('betslipBookie').set();
      cookieService.select('betslipStake').set();
      return;
    }

    cookieService.select('betslipBookie').set(selectedBookie?.bookmakerCode, new Date().getTime() + ONE_DAY);
    cookieService.select('betslipStake').set(String(stake), new Date().getTime() + ONE_DAY);
  }, [cookieService, betslip, selectedBookie, stake]);

  const callbacksRef = useRef<Record<string, CallbackFn[]>>({});
  const hasFetchedBetslipDataRef = useRef<boolean>(false);
  const shouldResetBetslipOnCloseRef = useRef<boolean>(false);
  const lastFetchStatusRef = useRef<LastFetchStatus | null>(null);
  // Latches "a BET_SLIP_VIEW is owed" across however many fetchBetslipData calls it takes to actually
  // land one. Set true by a call that reopens the betslip; consumed (fired + cleared) only by whichever
  // fetch eventually both (a) isn't superseded by a newer call and (b) actually applies bets to the
  // store. This survives the two ways firing it eagerly at the click site went wrong: a superseded
  // response (seq guard) or a failed request would otherwise drop the obligation entirely, and a
  // reopen whose fetch resolves to nothing applicable would otherwise fire a view event for an
  // accumulator that never actually rendered.
  const pendingViewTrackRef = useRef(false);

  // Delegate to getBetslipSnapshot so the derived shape (betIds, count) has a single source of truth
  // shared with non-React consumers. `storeState` changes reference on every setState, so the memo
  // stays in lockstep with the store.
  const betslipState = useMemo<BetslipState>(() => getBetslipSnapshot(storeState), [storeState]);

  const toggleAdded = useCallback(
    (val?: boolean): void => {
      if (typeof val !== 'undefined') {
        store.setState({ added: val });
        return;
      }

      store.setState({ added: !store.getState().added });
    },
    [store],
  );

  const setBetslipOpen = useCallback(
    (newOpenState: boolean) => {
      if (repubIs.GZ) onBetslipOpenChangeRef.current?.(newOpenState);

      // Belt-and-braces only — NOT what actually protects against a stale view-track obligation firing
      // against a hidden betslip. `open` has a second writer (MiniBetslipFromStore's header/mobile-nav
      // toggle sets the store field directly, bypassing this function entirely), so the real guard is
      // the live `store.getState().open` read at the point fetchBetslipData actually fires the event.
      if (!newOpenState) pendingViewTrackRef.current = false;

      store.setState({ open: newOpenState });
    },
    [repubIs.GZ, store],
  );

  const toggleBetslip = useCallback(
    (val?: boolean): void => {
      // Delegate to setBetslipOpen for both paths so the GZ scroll callback fires
      // exactly once — never inside a setState updater, which React may re-invoke.
      setBetslipOpen(typeof val !== 'undefined' ? val : !open);
    },
    [open, setBetslipOpen],
  );

  const changeStake = useCallback(
    (stake: number): void => {
      store.setState({ stake });
      cookieService.select('betslipStake').set(String(stake), new Date().getTime() + ONE_DAY);
    },
    [cookieService, store],
  );

  const syncModulesBetslipData = useCallback(() => {
    if (!betslip.length) return;
    callbacksRef.current.syncBetslip?.forEach(callback => callback(betslip, true));
  }, [betslip]);

  // Shared by fetchBetslipData's dead-IDs branch below and the __cxBetslip.clear() debug shim — both
  // need to wipe the persisted betslip cookie/localStorage in lockstep.
  const clearPersistedBetslip = useCallback(() => {
    cookieService.select('intBetslip').remove();
    localStorageService.select('intBetslip').set({});
  }, [cookieService, localStorageService]);

  // NOTE: it is essential we pass betslip data here
  // as this helper is used to initialise local state
  //
  // Returns whether the data was actually applied to the store (false when it bailed to an empty
  // in-memory betslip) — fetchBetslipData below uses this to decide whether a pending BET_SLIP_VIEW is
  // owed, since firing one for a fetch that stored nothing would report bets that never rendered.
  const storeBetslipData = useCallback(
    (data: Bet[], shouldSyncModulesBetslip?: boolean): boolean => {
      const { bookieOdds: newBookieOdds = [], selectedBookie: newSelectedBookie = null } =
        calculateBookieOddsAndSelectedBookieOdds(data, selectedBookie?.bookmakerCode);

      if (!newBookieOdds.length) {
        // Live bets that just don't resolve to a bookie right now (suspended odds, or a multi-bet
        // selection missing `uiOptions`) — reset in-memory state only. The intBetslip cookie is left
        // untouched so a refresh can recover once odds/uiOptions are present again; betslipBookie/
        // betslipStake still get cleared by the reactive effect above (pre-existing behavior — it
        // reacts to `betslip` becoming empty regardless of caller) — only the bet-ID list survives.
        store.setState({ betslip: [] });
        return false;
      }

      // assign state
      store.setState({ betslip: data, bookieOdds: newBookieOdds, selectedBookie: newSelectedBookie });

      // handle callbacks
      if (shouldSyncModulesBetslip) callbacksRef.current.syncBetslip?.forEach(callback => callback(data, true));
      return true;
    },
    [selectedBookie, store],
  );

  // Stamped per call and checked in the completion callback below so a response that resolves after a
  // newer fetchBetslipData call has already started (e.g. two quick clicks) is dropped rather than
  // applied out of order — otherwise a stale response can overwrite state (or, since this callback may
  // clear persisted storage, permanently destroy) a newer click just wrote.
  const fetchSeqRef = useRef(0);

  const fetchBetslipData = useCallback(
    // `shouldTrackView` latches pendingViewTrackRef rather than firing BET_SLIP_VIEW itself — this call
    // may be superseded before its response lands (seq guard below), may fail outright, or may resolve
    // to bets that storeBetslipData can't apply (see its own return value). Any of those would silently
    // drop the view event if it were tied to THIS call's response; latching the intent instead lets
    // whichever later fetchBetslipData call actually applies bets fire it, off ITS fresh, authoritative
    // list — never off the locally-known array at the click site (addOrRemoveFromBetslip only knows the
    // bet(s) it just added, not the full slip: mini-betslip.tsx's own trackBetSlip call reads the full
    // `betslip` from context for the same reason).
    (shouldSyncModulesBetslip?: boolean, shouldTrackView?: boolean): void => {
      if (shouldTrackView) pendingViewTrackRef.current = true;

      const seq = ++fetchSeqRef.current;
      store.setState({ loading: true });

      const { getBetslipAPI, getBetslipCookieArray, trackBetSlip } = betslipUtils;

      const betslipCookieArray = getBetslipCookieArray();

      const numberOfBets = betslipCookieArray.length;
      const betslipHasMultipleBets = numberOfBets > 1;
      const betslipHasSingleBet = numberOfBets === 1;

      if (betslipHasMultipleBets || betslipHasSingleBet) {
        const betIds = betslipCookieArray.map(bet => parseInt(bet[0], 10));
        getBetslipAPI(betIds, countryCode, subdivisionCode, (status, res) => {
          // Superseded by a newer fetchBetslipData call — do nothing, including to
          // pendingViewTrackRef: if it's latched, the newer call's own response is what will consume it.
          if (seq !== fetchSeqRef.current) return;

          lastFetchStatusRef.current = {
            ok: !!status?.success,
            reason: status?.success ? undefined : (res?.message ?? 'unknown'),
            at: Date.now(),
          };
          if (status.success && res.data[BETSLIPDATA]) {
            const bets = res.data[BETSLIPDATA].bets;
            if (!bets.length) {
              // Nothing came back at all for the bet IDs we asked for (settled/expired/removed
              // server-side) — clear the persisted cookie/localStorage so the next fetch doesn't
              // resend the same dead IDs. The betslip is now empty, so discharge any pending view-track
              // obligation too — there's nothing to report, and leaving it latched would let some later,
              // unrelated fetch fire it instead.
              clearPersistedBetslip();
              store.setState({ betslip: [], bookieOdds: [], selectedBookie: null });
              pendingViewTrackRef.current = false;
            } else {
              const applied = storeBetslipData(bets, shouldSyncModulesBetslip);
              if (applied && pendingViewTrackRef.current) {
                // Discharge unconditionally — this is the only point that resolves the obligation, so
                // it must not depend on how the betslip got closed. `open` can flip outside
                // setBetslipOpen (MiniBetslipFromStore's header/mobile-nav toggle writes the store's
                // `open` field directly), so clearing on close there too would still miss a THIRD
                // writer; gating the read here and discharging regardless of the outcome is what
                // actually closes it for every path. `store.getState().open` at fire time (not
                // "applied") is what decides whether there's still something to report.
                pendingViewTrackRef.current = false;
                if (store.getState().open) trackBetSlip(bets, store.getState().stake);
              }
            }
          }
          store.setState({ loading: false });
        });
      } else {
        lastFetchStatusRef.current = { ok: true, at: Date.now() };
        store.setState({ betslip: [], bookieOdds: [], selectedBookie: null, loading: false });
        // No bets in the cookie at all — nothing to view; see the dead-IDs branch above for why this
        // discharges rather than leaves a pending obligation standing.
        pendingViewTrackRef.current = false;
      }

      store.setState({ added: false });
      hasFetchedBetslipDataRef.current = true;
    },
    [betslipUtils, clearPersistedBetslip, countryCode, subdivisionCode, storeBetslipData, store],
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      // NOTE: server betslip initialisation is
      // supported on Atlas *only* as OC still
      // utilises legacy local storage API
      if (hasFetchedBetslipDataRef.current || !repubIs.OC) return;
      hasFetchedBetslipDataRef.current = true;
      fetchBetslipData();
    }, 2000);
    return () => {
      clearTimeout(timer);
    };
  }, [fetchBetslipData, repubIs]);

  // This is installed as window.bettingActions.addOrRemoveFromBetslip (see components/betslip.tsx),
  // the single sink every odds surface in the repo calls — racecards, verdicts, all four coupons,
  // Tip, odds-grid-external. It is where add_to_betslip/remove_bet come from for all of them, and it
  // must stay that way; only a caller that pushes them ITSELF opts out, via emitsOwnBetslipEvents.
  const addOrRemoveFromBetslip = useCallback(
    (
      betArray: any[],
      matchBetIds?: any[],
      bookie?: string,
      betOdds?: string,
      betName?: string,
      betLocation?: string,
      subeventName?: string,
      marketName?: string,
      eventName?: string,
      // Position 10 is `isAlreadySelected` at the odds-grid and Tip call sites and has never been read
      // here. Named rather than left off so the flag after it cannot quietly slide into its slot.
      _isAlreadySelected?: boolean,
      // NGF-6979/NGF-6983. Set only by the two instrumented odds-grid buttons, which push these two
      // events themselves so that OC gets them at all: that repub runs the legacy CDN betslip, so this
      // context never sees its clicks. Everywhere else the button stays silent and the push below is
      // the only one — hence an explicit opt-out rather than a betLocation check, which cannot work:
      // odds-grid-external sends the same 'odds-grid' betLocation and does NOT re-emit.
      emitsOwnBetslipEvents?: boolean,
    ): void => {
      store.setState({ loading: true });

      const { getBetslipCookieArray, getLocalStorageBetslip, postOddsClick } = betslipUtils;
      const cookieBetIdArray = getBetslipCookieArray();
      const localStorageBetslip = getLocalStorageBetslip();

      let betIndex: number;
      const trackingInput = { bookieCode: bookie, betOdds, betName, betLocation, subeventName, marketName, eventName };

      // Captured once, before the loop below can flip it — a multi-leg add (accumulator) must treat
      // "was the betslip closed" as a single fact for the whole call, not re-read it after the first
      // leg's own setBetslipOpen(true) has already changed it.
      const betslipWasClosed = !store.getState().open;
      // True once this call actually reopens the betslip (adds at least one new bet while it was
      // closed) — drives the BET_SLIP_VIEW tracking fired from fetchBetslipData below, off the fetch's
      // own authoritative bet list rather than the bet(s) this call just added. See the comment on
      // fetchBetslipData's shouldTrackView param.
      let betslipReopened = false;

      for (let i = 0; i < betArray.length; i++) {
        // Add or remove the ocBetId arg depending on its presence in the betIdArray.
        betIndex = cookieBetIdArray.map(item => item[0]).indexOf(betArray[i].betId.toString());

        if (betIndex === -1) {
          store.setState({ added: true });

          if (bookie) postOddsClick(betArray[i].betId, bookie, countryCode, subdivisionCode);

          // Push the bet into the array with a 1 for "selected".
          cookieBetIdArray.push([betArray[i].betId.toString(), '1']);
          if (betBasketCookieScope && betArray[i].marketId) {
            addToBetBasketCookie(betArray[i].betId, betArray[i].marketId, betBasketCookieScope);
          }
          // Add selectedBookie so it is retrievable via getBetSelectedBookieFromLocalStore()
          betArray[i].selectedBookie = bookie;
          localStorageBetslip[betArray[i].betId] = betArray[i];

          if (typeof matchBetIds !== 'undefined') {
            // eslint-disable-next-line array-callback-return
            matchBetIds.map(matchBet => {
              if (matchBet !== betArray[i].betId) {
                const removedBetIndex = cookieBetIdArray.map(item => item[0]).indexOf(matchBet.toString());
                if (removedBetIndex !== -1) {
                  cookieBetIdArray.splice(removedBetIndex, 1);
                  if (betBasketCookieScope) removeFromBetBasketCookie(Number(matchBet), betBasketCookieScope);
                  // Remove from localStorage
                  delete localStorageBetslip[matchBet];
                }
              }
            });
          }

          // Reopen on every new selection, not just the first — otherwise a bet added after the
          // betslip has been manually hidden stays hidden (NGF-7092 AC1). Guarded on the live open
          // state (not just called unconditionally) so a multi-leg add only flips it once: on GZ,
          // setBetslipOpen also scrolls the embedding parent page, and that must fire only on the
          // leg that actually transitions the betslip from closed to open, not once per leg.
          if (!store.getState().open) setBetslipOpen(true);
          if (betslipWasClosed) betslipReopened = true;
          if (!emitsOwnBetslipEvents) trackBetslipEvent(buildAddToBetslipEvent(trackingInput));
        } else {
          // Remove from cookie
          cookieBetIdArray.splice(betIndex, 1);
          if (betBasketCookieScope) removeFromBetBasketCookie(betArray[i].betId, betBasketCookieScope);

          // Remove from localStorage
          delete localStorageBetslip[betArray[i].betId];
          if (!emitsOwnBetslipEvents) trackBetslipEvent(buildRemoveBetEvent(trackingInput));
        }
      }

      if (cookieBetIdArray.length === 0) {
        setBetslipOpen(false);
      }

      // Store mini betslip in localStorage to make odds avaialbe for estimated betslip odds.
      localStorageService.select('intBetslip').set(localStorageBetslip);

      // Select and update cookie
      cookieService.select('intBetslip').setArrayString(cookieBetIdArray);

      // ensure we update state
      fetchBetslipData(undefined, betslipReopened);
    },
    [
      betBasketCookieScope,
      betslipUtils,
      countryCode,
      subdivisionCode,
      localStorageService,
      cookieService,
      fetchBetslipData,
      setBetslipOpen,
      store,
    ],
  );

  const removeBetFromBetslip = useCallback(
    (betId: number, disabled?: boolean, bet?: Bet): void => {
      const tempBetslip: Array<any> = [];
      for (let i = 0; i < betslip.length; i++) {
        let betWasRemoved = true;
        if (betslip[i].betId !== betId) {
          betWasRemoved = false;
          tempBetslip.push(betslip[i]);
        }

        if (betWasRemoved) {
          addOrRemoveFromBetslip(
            [{ betId }],
            [betId],
            selectedBookie?.bookmakerCode,
            selectedBookie?.oddsDecimal.toString(),
            bet?.name,
            'betslip',
            bet?.subeventName,
            bet?.marketName,
            'n/a',
          );
        }
      }
      if (!disabled) {
        let bookieExists = false;
        tempBetslip.forEach(bet => {
          bookieExists = bet.odds.some(
            (odds: { bookmakerCode: string }) => odds.bookmakerCode === (selectedBookie?.bookmakerCode ?? ''),
          );
        });

        if (!bookieExists) {
          store.setState({ selectedBookie: null });
          fetchBetslipData();
        }
      }

      callbacksRef.current.removeBet?.forEach(callback => callback(betId));
    },
    [addOrRemoveFromBetslip, betslip, selectedBookie, fetchBetslipData, store],
  );

  const handleClearBetslip = useCallback((): void => {
    // 1. tracking
    trackBetslipEvent(buildBetSlipClearEvent());
    trackBetslipEvent(buildBetSlipCloseEvent());

    // 2. close betslip and setup callbacks
    shouldResetBetslipOnCloseRef.current = true;
    setBetslipOpen(false);
  }, [setBetslipOpen]);

  useEffect(() => {
    if (open || !shouldResetBetslipOnCloseRef.current) return;
    shouldResetBetslipOnCloseRef.current = false;

    // 1. callbacks
    callbacksRef.current.clearBetslip?.forEach(callback => callback());

    // 2. clear betslip
    const bets = betslipUtils.getBetslipCookieArray();
    const modifiedBets = bets.map(bet => ({ betId: Number(bet[0]) }));
    addOrRemoveFromBetslip(modifiedBets);

    // 3. init state
    store.setState({ bookieOdds: [], betslip: [] });
  }, [open, betslipUtils, addOrRemoveFromBetslip, store]);

  const handleClickoutToPlaceBet = useCallback(
    (bookieFromBookieSelector?: BookieOdds): void => {
      try {
        const { getSelectedBookieBetOdds } = betslipUtils;
        const { betIds, betslip, count, selectedBookie: bookieFromSelectedBookie, stake } = betslipState;

        // 0. ensure we handle bookie selector list so
        // users can clickout to non-selected bookie,
        // i.e. those at the bottom of the betslip
        const selectedBookie: BookieOdds = bookieFromBookieSelector || bookieFromSelectedBookie!;
        const bookieCode = selectedBookie?.bookmakerCode;

        // 1. clickout
        // TODO(NGF-6314): scope this ambient window.ENV read for Astro-island compatibility. Deferred
        // from NGF-6300: BetslipProvider is not an Astro island, so it does not block the atlas island
        // work. NGF-6314 moves the clickout behind a shared api-service endpoint that injects
        // OC_API_KEY server-side, removing this window.ENV read entirely (and stopping OC_API_KEY being
        // shipped to the browser). Until then this stays as the RR7-only ambient read.
        const { CLICKOUT_URL, OC_API_KEY } = window.ENV;
        const apiKey = OC_API_KEY[repub as keyof typeof OC_API_KEY];
        // OC_API_KEY only maps a subset of repubs; a mount on an unmapped repub (e.g. WS) would
        // otherwise produce `apiKey=undefined` in the URL, which the clickout service rejects
        // silently. Surface a user-visible error modal (via an opaque reference code) instead of
        // letting "Place Bet" appear broken, and log the full cause for support/log correlation.
        if (!apiKey) {
          const code = getClickoutErrorCode('MISSING_API_KEY');
          console.error(`[betslip] ${CLICKOUT_ERROR_REASONS.MISSING_API_KEY} repub="${repub}" (ref ${code})`);
          store.setState({ clickoutErrorCode: code });
          return;
        }
        const betUrl = `${CLICKOUT_URL}/splash/clickout?betIds=${betIds.join('%2C')}&bookieCode=${encodeURIComponent(bookieCode)}&apiKey=${apiKey}`;
        clickout(betUrl, bookieCode, 'bet', true);

        // 2. tracking
        trackBetslipEvent(
          buildBetPlacedEvent({
            bookieName: bookmakers[bookieCode]?.bookmakerName,
            bookieCode,
            betStake: stake,
            betOdds: getSelectedBookieBetOdds(selectedBookie, userOddsFormat),
            betAccumulator: count > 1,
            count,
            betNames: betslip.map(betData => betData.name),
            marketNames: betslip.map(betData => betData.marketName),
            subeventNames: betslip.map(betData => betData.subeventName),
          }),
        );
      } catch (error) {
        if (error instanceof Error) {
          console.error(error);
          throw new Error(error.message);
        } else {
          throw error;
        }
      }
    },
    [repub, userOddsFormat, bookmakers, betslipUtils, betslipState, store],
  );

  const dismissClickoutError = useCallback((): void => {
    store.setState({ clickoutErrorCode: null });
  }, [store]);

  const selectBookie = useCallback(
    (idx: number): void => {
      const newSelectedBookie = bookieOdds[idx];
      store.setState({ selectedBookie: newSelectedBookie });

      cookieService.select('betslipBookie').set(newSelectedBookie.bookmakerCode, new Date().getTime() + ONE_DAY);

      trackBetslipEvent(buildChangeBookieSelectionEvent());
    },
    [bookieOdds, cookieService, store],
  );

  // Diagnostic shim for automated tests on non-OC repubs. Gated behind
  // ?cxDebug=1 / cxDebug=1 cookie so it never leaks into production traffic.
  // Intentionally NOT a trigger surface — tests should drive the UI via the
  // ARIA contract documented at the top of packages/features/betslip/src/components/
  // betslip.tsx. This shim only exposes what ARIA can't:
  //   clear()           — teardown (wipes betslip cookies + localStorage + state)
  //   lastFetchStatus() — last /sports-catalogue/v1/betslip outcome, for
  //                       diagnosing why aria-busy never flipped to false.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (repubIs.OC) return; // OC repub owns window.ocBetslip; do not collide
    if (!isCxDebugEnabled()) return;

    window.__cxBetslip = {
      clear: () => {
        clearPersistedBetslip();
        cookieService.select('betslipBookie').set();
        cookieService.select('betslipStake').set();
        fetchBetslipData();
      },
      lastFetchStatus: () => lastFetchStatusRef.current,
    };

    return () => {
      delete window.__cxBetslip;
    };
  }, [repubIs.OC, cookieService, clearPersistedBetslip, fetchBetslipData]);

  const value = useMemo(
    () => ({
      bookmakers,
      callbacksRef,
      oddsFormatIs,
      repub,
      userOddsFormat,
      ...betslipUtils,
      ...betslipState,
      toggleAdded,
      toggleBetslip,
      changeStake,
      syncModulesBetslipData,
      fetchBetslipData,
      storeBetslipData,
      handleClearBetslip,
      handleClickoutToPlaceBet,
      dismissClickoutError,
      addOrRemoveFromBetslip,
      removeBetFromBetslip,
      selectBookie,
    }),
    [
      bookmakers,
      callbacksRef,
      oddsFormatIs,
      repub,
      userOddsFormat,
      betslipUtils,
      betslipState,
      toggleAdded,
      toggleBetslip,
      changeStake,
      syncModulesBetslipData,
      fetchBetslipData,
      storeBetslipData,
      handleClearBetslip,
      handleClickoutToPlaceBet,
      dismissClickoutError,
      addOrRemoveFromBetslip,
      removeBetFromBetslip,
      selectBookie,
    ],
  );

  return (
    <BetslipContext.Provider value={value} {...props}>
      {children}
    </BetslipContext.Provider>
  );
};

export const useBetslipContext = () => {
  const context = useContext(BetslipContext);
  if (!context) {
    throw new Error('useBetslipContext can only be used inside of BetslipProvider');
  }

  return context;
};
